import { useEffect, useMemo, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { findTuto, tutoSources, type TutoId } from './tutorial-catalog';

type TutorialPlayerDialogProps = {
  tutoId: TutoId | null;
  onClose: () => void;
};

const isPortrait = (): boolean => {
  try {
    return window.matchMedia('(orientation: portrait)').matches;
  } catch {
    return false;
  }
};

/** Plein écran natif si possible ; sinon la modale couvre déjà tout l'écran. */
async function enterFullscreen(el: HTMLElement): Promise<boolean> {
  if (document.fullscreenElement || typeof el.requestFullscreen !== 'function') return false;
  try {
    await el.requestFullscreen();
    return true;
  } catch {
    return false;
  }
}

function exitFullscreenIfOwned(owned: boolean) {
  if (!owned || !document.fullscreenElement || typeof document.exitFullscreen !== 'function') {
    return;
  }
  document.exitFullscreen().catch(() => undefined);
}

/**
 * Lecteur des tutos : modale noire plein écran (jamais window.open, qui fait
 * planter la WebView Capacitor). 16:9 en paysage (caisse, ordinateur), 9:16 en
 * portrait (téléphone). Lecture et plein écran au lancement, fermeture
 * automatique à la fin : retour à l'écran d'origine.
 */
export function TutorialPlayerDialog({ tutoId, onClose }: TutorialPlayerDialogProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ownsFullscreen = useRef(false);
  // Format choisi à l'ouverture : une rotation en cours de lecture ne relance pas la vidéo.
  const portrait = useMemo(() => tutoId !== null && isPortrait(), [tutoId]);
  const open = tutoId !== null;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    // Le clic d'ouverture vaut geste utilisateur : plein écran + lecture autorisés.
    const timer = window.setTimeout(() => {
      const content = contentRef.current;
      if (content) {
        void enterFullscreen(content).then((owned) => {
          if (cancelled) exitFullscreenIfOwned(owned);
          else ownsFullscreen.current = owned;
        });
      }
      const playing = videoRef.current?.play();
      if (playing) playing.catch(() => undefined); // lecture bloquée : bouton Lecture
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      exitFullscreenIfOwned(ownsFullscreen.current);
      ownsFullscreen.current = false;
    };
  }, [open, tutoId]);

  if (!tutoId) return null;
  const tuto = findTuto(tutoId);
  const { src, poster } = tutoSources(tutoId, portrait);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black" />
        <DialogPrimitive.Content
          ref={contentRef}
          aria-describedby={undefined}
          className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-black text-white outline-none"
          data-testid="tutorial-player"
        >
          <DialogPrimitive.Title className="sr-only">Tutoriel : {tuto.title}</DialogPrimitive.Title>
          <video
            key={src}
            ref={videoRef}
            src={src}
            poster={poster}
            controls
            playsInline
            autoPlay
            preload="metadata"
            controlsList="nodownload"
            className="h-full w-full bg-black object-contain"
            onEnded={onClose}
          />
          <DialogPrimitive.Close
            className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white"
            aria-label="Fermer le tutoriel"
          >
            <X className="h-6 w-6" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}
